import { UserRole } from '../types';

export enum AccessControlArea {
  DASHBOARD = 'dashboard',
  SALES = 'sales',
  ACCOMMODATION = 'accommodation',
  ACCOUNTING = 'accounting',
  INVENTORY = 'inventory',
  SETTINGS = 'settings',
}

export enum AccessControlCode {
  DASHBOARD_PAGE_ACCESS = 'dashboard.page.access',
  DASHBOARD_DEPARTMENTS_CREATE = 'dashboard.departments.create',
  DASHBOARD_MENU_MANAGE = 'dashboard.menu.manage',
  DASHBOARD_SECTIONS_TABLES_MANAGE = 'dashboard.sections_tables.manage',
  DASHBOARD_OUTLETS_MANAGE = 'dashboard.outlets.manage',
  DASHBOARD_CLIENTS_VIEW = 'dashboard.clients.view',
  DASHBOARD_USERS_MANAGE = 'dashboard.users.manage',
  DASHBOARD_SALES_REPORTS_VIEW = 'dashboard.sales_reports.view',
  DASHBOARD_SECURITY_ACCESS = 'dashboard.security.access',
  DASHBOARD_SETTINGS_ACCESS = 'dashboard.settings.access',
  DASHBOARD_BILLINGS_ACCESS = 'dashboard.billings.access',
  DASHBOARD_MULTIPLE_OUTLETS_ACCESS = 'dashboard.multiple_outlets.access',
  DASHBOARD_EMAIL_CONFIG_MANAGE = 'dashboard.email_config.manage',
  DASHBOARD_KDS_ACCESS = 'dashboard.kds.access',
  SALES_DAY_OPEN = 'sales.day.open',
  SALES_ORDERS_CREATE = 'sales.orders.create',
  SALES_ORDER_ITEMS_DELETE_PENDING = 'sales.order_items.delete_pending',
  SALES_ORDER_ITEMS_SHIFT = 'sales.order_items.shift',
  SALES_DISCOUNTS_OFFER = 'sales.discounts.offer',
  SALES_ORDER_ITEMS_CANCEL = 'sales.order_items.cancel',
  SALES_BILLS_PRINT = 'sales.bills.print',
  SALES_BILLS_REPRINT = 'sales.bills.reprint',
  SALES_KOTS_REPRINT = 'sales.kots.reprint',
  SALES_EBM_OVERRIDE_UNCONFIRMED = 'sales.ebm.override_unconfirmed',
  ACCOMMODATION_PAGE_ACCESS = 'accommodation.page.access',
  ACCOMMODATION_ROOMS_MANAGE = 'accommodation.rooms.manage',
  ACCOMMODATION_BOOKINGS_CREATE = 'accommodation.bookings.create',
  ACCOMMODATION_BOOKINGS_MANAGE = 'accommodation.bookings.manage',
  ACCOMMODATION_BOOKINGS_FINANCIAL_EDIT = 'accommodation.bookings.financial_edit',
  ACCOMMODATION_GUESTS_VIEW = 'accommodation.guests.view',
  ACCOMMODATION_REPORTS_VIEW = 'accommodation.reports.view',
  ACCOMMODATION_REPORTS_OCCUPANCY = 'accommodation.reports.occupancy',
  ACCOMMODATION_REPORTS_REVENUE = 'accommodation.reports.revenue',
  ACCOMMODATION_REPORTS_BOOKING_SOURCE = 'accommodation.reports.booking_source',
  ACCOMMODATION_REPORTS_NIGHT_AUDIT = 'accommodation.reports.night_audit',
  ACCOMMODATION_REPORTS_ROOM_REVENUE = 'accommodation.reports.room_revenue',
  ACCOMMODATION_REPORTS_REVPAR = 'accommodation.reports.revpar',
  ACCOMMODATION_REVIEWS_VIEW = 'accommodation.reviews.view',
  ACCOMMODATION_SETTINGS_MANAGE = 'accommodation.settings.manage',
  ACCOUNTING_VIEW_ACCOUNTS = 'accounting.view_accounts',
  ACCOUNTING_MANAGE_ACCOUNTS = 'accounting.manage_accounts',
  ACCOUNTING_VIEW_JOURNALS = 'accounting.view_journals',
  ACCOUNTING_POST_JOURNALS = 'accounting.post_journals',
  ACCOUNTING_REVERSE_JOURNALS = 'accounting.reverse_journals',
  ACCOUNTING_CLOSE_PERIODS = 'accounting.close_periods',
  ACCOUNTING_VIEW_REPORTS = 'accounting.view_reports',
  ACCOUNTING_EXPORT_REPORTS = 'accounting.export_reports',
  ACCOUNTING_MANAGE_TAX_RATES = 'accounting.manage_tax_rates',
  ACCOUNTING_MANAGE_PAYMENT_MAPPINGS = 'accounting.manage_payment_mappings',
  INVENTORY_PAGE_ACCESS = 'inventory.page_access',
  INVENTORY_ITEMS_VIEW = 'inventory.items_view',
  INVENTORY_ITEMS_MANAGE = 'inventory.items_manage',
  INVENTORY_SUPPLIERS_VIEW = 'inventory.suppliers_view',
  INVENTORY_SUPPLIERS_MANAGE = 'inventory.suppliers_manage',
  INVENTORY_INVOICES_VIEW = 'inventory.invoices_view',
  INVENTORY_INVOICES_MANAGE = 'inventory.invoices_manage',
  INVENTORY_INVOICES_POST = 'inventory.invoices_post',
  INVENTORY_PAYMENTS_VIEW = 'inventory.payments_view',
  INVENTORY_PAYMENTS_RECORD = 'inventory.payments_record',
  INVENTORY_STOCK_VIEW = 'inventory.stock_view',
  INVENTORY_STOCK_ADJUST = 'inventory.stock_adjust',
  INVENTORY_STOCK_TRANSFER = 'inventory.stock_transfer',
  INVENTORY_RECIPES_VIEW = 'inventory.recipes_view',
  INVENTORY_RECIPES_MANAGE = 'inventory.recipes_manage',
  INVENTORY_REPORTS_VIEW = 'inventory.reports_view',
  SETTINGS_PAGE_ACCESS = 'settings.page.access',
  SETTINGS_USERS_MANAGE = 'settings.users.manage',
  SETTINGS_ROLES_MANAGE = 'settings.roles.manage',
  SETTINGS_SECURITY_ACCESS_CONTROLS_VIEW = 'settings.security.access_controls.view',
  SETTINGS_SECURITY_POLICIES_MANAGE = 'settings.security.policies.manage',
  SETTINGS_SECURITY_ROLE_ASSIGNMENTS_MANAGE = 'settings.security.role_assignments.manage',
  SETTINGS_SECURITY_REGISTERED_DEVICES_MANAGE = 'settings.security.registered_devices.manage',
  SETTINGS_EMAIL_CONFIG_MANAGE = 'settings.email_config.manage',
  SETTINGS_EBM_CONFIG_MANAGE = 'settings.ebm.config_manage',
  SETTINGS_EBM_FISCAL_SETUP_MANAGE = 'settings.ebm.fiscal_setup_manage',
  SETTINGS_EBM_SALE_RECORDS_MANAGE = 'settings.ebm.sale_records_manage',
}

export interface AccessControlDefinition {
  code: AccessControlCode;
  name: string;
  description: string;
  area: AccessControlArea;
  sortOrder: number;
}

export const ACCESS_CONTROL_DEFINITIONS: readonly AccessControlDefinition[] = [
  {
    code: AccessControlCode.DASHBOARD_PAGE_ACCESS,
    name: 'Access dashboard page',
    description:
      'Allows the user to open and use the dashboard area of the app.',
    area: AccessControlArea.DASHBOARD,
    sortOrder: 10,
  },
  {
    code: AccessControlCode.DASHBOARD_DEPARTMENTS_CREATE,
    name: 'Create departments',
    description:
      'Allows the user to create new outlet departments from the dashboard.',
    area: AccessControlArea.DASHBOARD,
    sortOrder: 20,
  },
  {
    code: AccessControlCode.DASHBOARD_MENU_MANAGE,
    name: 'Manage menu items',
    description:
      'Allows the user to create, update, and remove menu categories, menu items, and add-ons.',
    area: AccessControlArea.DASHBOARD,
    sortOrder: 30,
  },
  {
    code: AccessControlCode.DASHBOARD_SECTIONS_TABLES_MANAGE,
    name: 'Manage sections and tables',
    description:
      'Allows the user to create and update outlet sections, tables, and printer setup from the dashboard.',
    area: AccessControlArea.DASHBOARD,
    sortOrder: 40,
  },
  {
    code: AccessControlCode.DASHBOARD_OUTLETS_MANAGE,
    name: 'Manage outlets',
    description:
      'Allows the user to create, update, and manage outlet details.',
    area: AccessControlArea.DASHBOARD,
    sortOrder: 50,
  },
  {
    code: AccessControlCode.DASHBOARD_CLIENTS_VIEW,
    name: 'View clients',
    description:
      'Allows the user to access company client records in the dashboard.',
    area: AccessControlArea.DASHBOARD,
    sortOrder: 60,
  },
  {
    code: AccessControlCode.DASHBOARD_USERS_MANAGE,
    name: 'Manage user accounts',
    description:
      'Allows the user to create, invite, disable, and manage employee user accounts.',
    area: AccessControlArea.DASHBOARD,
    sortOrder: 70,
  },
  {
    code: AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW,
    name: 'View sales reports',
    description:
      'Allows the user to open dashboard sales reports and related financial reporting pages.',
    area: AccessControlArea.DASHBOARD,
    sortOrder: 80,
  },
  {
    code: AccessControlCode.DASHBOARD_SECURITY_ACCESS,
    name: 'Access security page',
    description:
      'Allows the user to open the security area and manage access policies and permissions.',
    area: AccessControlArea.DASHBOARD,
    sortOrder: 90,
  },
  {
    code: AccessControlCode.DASHBOARD_SETTINGS_ACCESS,
    name: 'Access settings page',
    description:
      'Allows the user to open the company settings area in the dashboard.',
    area: AccessControlArea.DASHBOARD,
    sortOrder: 100,
  },
  {
    code: AccessControlCode.DASHBOARD_BILLINGS_ACCESS,
    name: 'Access billings page',
    description:
      'Allows the user to open billing and subscription management pages.',
    area: AccessControlArea.DASHBOARD,
    sortOrder: 110,
  },
  {
    code: AccessControlCode.DASHBOARD_MULTIPLE_OUTLETS_ACCESS,
    name: 'Access multiple outlets',
    description:
      'Allows the user to switch outlet context and work across more than one outlet.',
    area: AccessControlArea.DASHBOARD,
    sortOrder: 120,
  },
  {
    code: AccessControlCode.DASHBOARD_EMAIL_CONFIG_MANAGE,
    name: 'Manage email configurations',
    description:
      'Allows the user to add, update, and remove email notification recipients.',
    area: AccessControlArea.DASHBOARD,
    sortOrder: 125,
  },
  {
    code: AccessControlCode.DASHBOARD_KDS_ACCESS,
    name: 'Access Kitchen Display Screen',
    description:
      'Allows the user to open the Kitchen Display Screen for tracking KOT tickets in real time.',
    area: AccessControlArea.DASHBOARD,
    sortOrder: 126,
  },
  {
    code: AccessControlCode.SALES_DAY_OPEN,
    name: 'Open a new day',
    description:
      'Allows the user to open a new business day from the sales page.',
    area: AccessControlArea.SALES,
    sortOrder: 130,
  },
  {
    code: AccessControlCode.SALES_ORDERS_CREATE,
    name: 'Create orders',
    description:
      'Allows the user to create new dine-in, takeaway, or cash orders from the sales page.',
    area: AccessControlArea.SALES,
    sortOrder: 140,
  },
  {
    code: AccessControlCode.SALES_ORDER_ITEMS_DELETE_PENDING,
    name: 'Delete pending order items',
    description:
      'Allows the user to remove order items before they are confirmed to a KOT.',
    area: AccessControlArea.SALES,
    sortOrder: 150,
  },
  {
    code: AccessControlCode.SALES_ORDER_ITEMS_SHIFT,
    name: 'Shift items between tables',
    description:
      'Allows the user to move confirmed order items between tables and running orders.',
    area: AccessControlArea.SALES,
    sortOrder: 160,
  },
  {
    code: AccessControlCode.SALES_DISCOUNTS_OFFER,
    name: 'Offer discounts',
    description:
      'Allows the user to add, replace, or remove discounts on running orders.',
    area: AccessControlArea.SALES,
    sortOrder: 170,
  },
  {
    code: AccessControlCode.SALES_ORDER_ITEMS_CANCEL,
    name: 'Cancel order items',
    description:
      'Allows the user to cancel already confirmed order items and record a reason.',
    area: AccessControlArea.SALES,
    sortOrder: 180,
  },
  {
    code: AccessControlCode.SALES_BILLS_PRINT,
    name: 'Print bills',
    description: 'Allows the user to print customer bills from the sales page.',
    area: AccessControlArea.SALES,
    sortOrder: 185,
  },
  {
    code: AccessControlCode.SALES_BILLS_REPRINT,
    name: 'Re-print bills',
    description: 'Allows the user to re-print customer bills.',
    area: AccessControlArea.SALES,
    sortOrder: 190,
  },
  {
    code: AccessControlCode.SALES_KOTS_REPRINT,
    name: 'Re-print KOTs',
    description: 'Allows the user to re-print kitchen order tickets.',
    area: AccessControlArea.SALES,
    sortOrder: 200,
  },
  {
    code: AccessControlCode.SALES_EBM_OVERRIDE_UNCONFIRMED,
    name: 'Override unconfirmed EBM sale',
    description:
      'Allows the user to accept a sale despite RRA fiscal confirmation failure, after bounded retries have been exhausted.',
    area: AccessControlArea.SALES,
    sortOrder: 205,
  },
  {
    code: AccessControlCode.ACCOMMODATION_PAGE_ACCESS,
    name: 'Access accommodation',
    description: 'Allows the user to open the accommodation module.',
    area: AccessControlArea.ACCOMMODATION,
    sortOrder: 210,
  },
  {
    code: AccessControlCode.ACCOMMODATION_ROOMS_MANAGE,
    name: 'Manage rooms',
    description:
      'Allows the user to create, update, and deactivate room types and individual rooms.',
    area: AccessControlArea.ACCOMMODATION,
    sortOrder: 220,
  },
  {
    code: AccessControlCode.ACCOMMODATION_BOOKINGS_CREATE,
    name: 'Create bookings',
    description: 'Allows the user to create new reservations.',
    area: AccessControlArea.ACCOMMODATION,
    sortOrder: 230,
  },
  {
    code: AccessControlCode.ACCOMMODATION_BOOKINGS_MANAGE,
    name: 'Manage bookings',
    description:
      'Allows the user to modify, check in, check out, and cancel reservations.',
    area: AccessControlArea.ACCOMMODATION,
    sortOrder: 240,
  },
  {
    code: AccessControlCode.ACCOMMODATION_BOOKINGS_FINANCIAL_EDIT,
    name: 'Correct booking rates and payments',
    description:
      'Allows the user to change room rates and edit or delete payments on bookings that have not been checked in. Every change is logged and emailed to accommodation notification recipients.',
    area: AccessControlArea.ACCOMMODATION,
    sortOrder: 245,
  },
  {
    code: AccessControlCode.ACCOMMODATION_GUESTS_VIEW,
    name: 'View accommodation guests',
    description:
      'Allows the user to access the accommodation guests list and guest profiles.',
    area: AccessControlArea.ACCOMMODATION,
    sortOrder: 235,
  },
  {
    code: AccessControlCode.ACCOMMODATION_REPORTS_VIEW,
    name: 'View accommodation reports',
    description: 'Allows the user to view occupancy and revenue reports.',
    area: AccessControlArea.ACCOMMODATION,
    sortOrder: 250,
  },
  {
    code: AccessControlCode.ACCOMMODATION_REPORTS_OCCUPANCY,
    name: 'View occupancy report',
    description:
      'Allows the user to view the accommodation occupancy and room utilisation report.',
    area: AccessControlArea.ACCOMMODATION,
    sortOrder: 252,
  },
  {
    code: AccessControlCode.ACCOMMODATION_REPORTS_REVENUE,
    name: 'View revenue report',
    description:
      'Allows the user to view booking revenue, collected amounts and outstanding balances.',
    area: AccessControlArea.ACCOMMODATION,
    sortOrder: 254,
  },
  {
    code: AccessControlCode.ACCOMMODATION_REPORTS_BOOKING_SOURCE,
    name: 'View booking source report',
    description:
      'Allows the user to view revenue and booking counts broken down by booking source.',
    area: AccessControlArea.ACCOMMODATION,
    sortOrder: 256,
  },
  {
    code: AccessControlCode.ACCOMMODATION_REPORTS_NIGHT_AUDIT,
    name: 'View night audit report',
    description:
      'Allows the user to view the daily night audit (check-ins, check-outs, revenue).',
    area: AccessControlArea.ACCOMMODATION,
    sortOrder: 258,
  },
  {
    code: AccessControlCode.ACCOMMODATION_REPORTS_ROOM_REVENUE,
    name: 'View room revenue report',
    description:
      'Allows the user to view the room-by-room daily revenue matrix report.',
    area: AccessControlArea.ACCOMMODATION,
    sortOrder: 259,
  },
  {
    code: AccessControlCode.ACCOMMODATION_REPORTS_REVPAR,
    name: 'View RevPAR report',
    description:
      'Allows the user to view the Revenue Per Available Room (RevPAR) report and trends.',
    area: AccessControlArea.ACCOMMODATION,
    sortOrder: 260,
  },
  {
    code: AccessControlCode.ACCOMMODATION_REVIEWS_VIEW,
    name: 'View guest reviews',
    description:
      'Allows the user to view and analyse guest review submissions for accommodation stays.',
    area: AccessControlArea.ACCOMMODATION,
    sortOrder: 265,
  },
  {
    code: AccessControlCode.ACCOMMODATION_SETTINGS_MANAGE,
    name: 'Manage property settings',
    description:
      'Allows the user to configure property details and rate plans.',
    area: AccessControlArea.ACCOMMODATION,
    sortOrder: 270,
  },
  {
    code: AccessControlCode.ACCOUNTING_VIEW_ACCOUNTS,
    name: 'View chart of accounts',
    description:
      'Allows the user to view the company chart of accounts and account balances.',
    area: AccessControlArea.ACCOUNTING,
    sortOrder: 280,
  },
  {
    code: AccessControlCode.ACCOUNTING_MANAGE_ACCOUNTS,
    name: 'Manage chart of accounts',
    description:
      'Allows the user to create, edit, and deactivate chart of accounts records.',
    area: AccessControlArea.ACCOUNTING,
    sortOrder: 290,
  },
  {
    code: AccessControlCode.ACCOUNTING_VIEW_JOURNALS,
    name: 'View journal entries',
    description:
      'Allows the user to view manual and automated accounting journal entries.',
    area: AccessControlArea.ACCOUNTING,
    sortOrder: 300,
  },
  {
    code: AccessControlCode.ACCOUNTING_POST_JOURNALS,
    name: 'Post journal entries',
    description:
      'Allows the user to post balanced draft journal entries into the ledger.',
    area: AccessControlArea.ACCOUNTING,
    sortOrder: 310,
  },
  {
    code: AccessControlCode.ACCOUNTING_REVERSE_JOURNALS,
    name: 'Reverse journal entries',
    description:
      'Allows the user to reverse posted journal entries using the reversal workflow.',
    area: AccessControlArea.ACCOUNTING,
    sortOrder: 320,
  },
  {
    code: AccessControlCode.ACCOUNTING_CLOSE_PERIODS,
    name: 'Close accounting periods',
    description:
      'Allows the user to close open accounting periods and lock new postings.',
    area: AccessControlArea.ACCOUNTING,
    sortOrder: 330,
  },
  {
    code: AccessControlCode.ACCOUNTING_VIEW_REPORTS,
    name: 'View accounting reports',
    description:
      'Allows the user to view ledgers, trial balances, and other accounting reports.',
    area: AccessControlArea.ACCOUNTING,
    sortOrder: 340,
  },
  {
    code: AccessControlCode.ACCOUNTING_EXPORT_REPORTS,
    name: 'Export accounting reports',
    description:
      'Allows the user to export accounting reports for sharing or offline analysis.',
    area: AccessControlArea.ACCOUNTING,
    sortOrder: 350,
  },
  {
    code: AccessControlCode.ACCOUNTING_MANAGE_TAX_RATES,
    name: 'Manage tax rates',
    description:
      'Allows the user to configure accounting tax rates and tax account mappings.',
    area: AccessControlArea.ACCOUNTING,
    sortOrder: 360,
  },
  {
    code: AccessControlCode.ACCOUNTING_MANAGE_PAYMENT_MAPPINGS,
    name: 'Manage payment mappings',
    description:
      'Allows the user to configure payment method to account mapping rules.',
    area: AccessControlArea.ACCOUNTING,
    sortOrder: 370,
  },
  {
    code: AccessControlCode.INVENTORY_PAGE_ACCESS,
    name: 'Access inventory module',
    description: 'Allows the user to open the inventory management area.',
    area: AccessControlArea.INVENTORY,
    sortOrder: 380,
  },
  {
    code: AccessControlCode.INVENTORY_ITEMS_VIEW,
    name: 'View inventory items',
    description:
      'Allows the user to view the company inventory item catalogue.',
    area: AccessControlArea.INVENTORY,
    sortOrder: 390,
  },
  {
    code: AccessControlCode.INVENTORY_ITEMS_MANAGE,
    name: 'Manage inventory items',
    description:
      'Allows the user to create, edit, and deactivate inventory items.',
    area: AccessControlArea.INVENTORY,
    sortOrder: 400,
  },
  {
    code: AccessControlCode.INVENTORY_SUPPLIERS_VIEW,
    name: 'View suppliers',
    description: 'Allows the user to view the company supplier list.',
    area: AccessControlArea.INVENTORY,
    sortOrder: 410,
  },
  {
    code: AccessControlCode.INVENTORY_SUPPLIERS_MANAGE,
    name: 'Manage suppliers',
    description: 'Allows the user to create, edit, and deactivate suppliers.',
    area: AccessControlArea.INVENTORY,
    sortOrder: 420,
  },
  {
    code: AccessControlCode.INVENTORY_INVOICES_VIEW,
    name: 'View supplier invoices',
    description: 'Allows the user to view supplier purchase invoices.',
    area: AccessControlArea.INVENTORY,
    sortOrder: 430,
  },
  {
    code: AccessControlCode.INVENTORY_INVOICES_MANAGE,
    name: 'Manage supplier invoices',
    description: 'Allows the user to create and edit draft supplier invoices.',
    area: AccessControlArea.INVENTORY,
    sortOrder: 440,
  },
  {
    code: AccessControlCode.INVENTORY_INVOICES_POST,
    name: 'Post supplier invoices',
    description:
      'Allows the user to post draft supplier invoices to stock and accounting.',
    area: AccessControlArea.INVENTORY,
    sortOrder: 450,
  },
  {
    code: AccessControlCode.INVENTORY_PAYMENTS_VIEW,
    name: 'View supplier payments',
    description: 'Allows the user to view recorded supplier payments.',
    area: AccessControlArea.INVENTORY,
    sortOrder: 460,
  },
  {
    code: AccessControlCode.INVENTORY_PAYMENTS_RECORD,
    name: 'Record supplier payments',
    description:
      'Allows the user to record new payments against supplier invoices.',
    area: AccessControlArea.INVENTORY,
    sortOrder: 470,
  },
  {
    code: AccessControlCode.INVENTORY_STOCK_VIEW,
    name: 'View stock levels',
    description:
      'Allows the user to view stock balances, locations, and movement history.',
    area: AccessControlArea.INVENTORY,
    sortOrder: 480,
  },
  {
    code: AccessControlCode.INVENTORY_STOCK_ADJUST,
    name: 'Adjust stock',
    description: 'Allows the user to record stock adjustments and wastage.',
    area: AccessControlArea.INVENTORY,
    sortOrder: 490,
  },
  {
    code: AccessControlCode.INVENTORY_STOCK_TRANSFER,
    name: 'Transfer stock',
    description: 'Allows the user to create stock transfers between locations.',
    area: AccessControlArea.INVENTORY,
    sortOrder: 500,
  },
  {
    code: AccessControlCode.INVENTORY_RECIPES_VIEW,
    name: 'View recipes',
    description:
      'Allows the user to view inventory recipes linked to menu items.',
    area: AccessControlArea.INVENTORY,
    sortOrder: 510,
  },
  {
    code: AccessControlCode.INVENTORY_RECIPES_MANAGE,
    name: 'Manage recipes',
    description:
      'Allows the user to create, edit, and delete inventory recipes.',
    area: AccessControlArea.INVENTORY,
    sortOrder: 520,
  },
  {
    code: AccessControlCode.INVENTORY_REPORTS_VIEW,
    name: 'View inventory reports',
    description:
      'Allows the user to view stock valuation and inventory movement reports.',
    area: AccessControlArea.INVENTORY,
    sortOrder: 530,
  },
  {
    code: AccessControlCode.SETTINGS_PAGE_ACCESS,
    name: 'Access settings module',
    description:
      'Allows the user to open the Settings module (users, security, notifications, and fiscal device setup).',
    area: AccessControlArea.SETTINGS,
    sortOrder: 540,
  },
  {
    code: AccessControlCode.SETTINGS_USERS_MANAGE,
    name: 'Manage user accounts',
    description:
      'Allows the user to create, invite, disable, and manage employee user accounts from Settings.',
    area: AccessControlArea.SETTINGS,
    sortOrder: 541,
  },
  {
    code: AccessControlCode.SETTINGS_ROLES_MANAGE,
    name: 'Manage roles',
    description:
      'Allows the user to manage user roles and role policy assignments from Settings.',
    area: AccessControlArea.SETTINGS,
    sortOrder: 542,
  },
  {
    code: AccessControlCode.SETTINGS_SECURITY_ACCESS_CONTROLS_VIEW,
    name: 'View access control catalog',
    description:
      'Allows the user to view the global access control catalog under Settings > Security.',
    area: AccessControlArea.SETTINGS,
    sortOrder: 543,
  },
  {
    code: AccessControlCode.SETTINGS_SECURITY_POLICIES_MANAGE,
    name: 'Manage access policies',
    description:
      'Allows the user to create and manage company access policies under Settings > Security.',
    area: AccessControlArea.SETTINGS,
    sortOrder: 544,
  },
  {
    code: AccessControlCode.SETTINGS_SECURITY_ROLE_ASSIGNMENTS_MANAGE,
    name: 'Manage role assignments',
    description:
      'Allows the user to assign access policies to outlet roles under Settings > Security.',
    area: AccessControlArea.SETTINGS,
    sortOrder: 545,
  },
  {
    code: AccessControlCode.SETTINGS_SECURITY_REGISTERED_DEVICES_MANAGE,
    name: 'Manage registered devices',
    description:
      'Allows the user to view and manage registered devices under Settings > Security.',
    area: AccessControlArea.SETTINGS,
    sortOrder: 546,
  },
  {
    code: AccessControlCode.SETTINGS_EMAIL_CONFIG_MANAGE,
    name: 'Manage email configurations',
    description:
      'Allows the user to add, update, and remove email notification recipients from Settings.',
    area: AccessControlArea.SETTINGS,
    sortOrder: 547,
  },
  {
    code: AccessControlCode.SETTINGS_EBM_CONFIG_MANAGE,
    name: 'Manage EBM device configuration',
    description:
      'Allows the user to configure and activate the RRA fiscal (EBM) device for an outlet.',
    area: AccessControlArea.SETTINGS,
    sortOrder: 548,
  },
  {
    code: AccessControlCode.SETTINGS_EBM_FISCAL_SETUP_MANAGE,
    name: 'Manage menu item fiscal setup',
    description:
      'Allows the user to fix menu items missing an EBM tax category or item code.',
    area: AccessControlArea.SETTINGS,
    sortOrder: 549,
  },
  {
    code: AccessControlCode.SETTINGS_EBM_SALE_RECORDS_MANAGE,
    name: 'Manage EBM sale records',
    description:
      'Allows the user to view and retry failed or pending EBM sale submissions.',
    area: AccessControlArea.SETTINGS,
    sortOrder: 550,
  },
] as const;

export const FULL_ACCESS_USER_ROLES: readonly UserRole[] = [
  UserRole.SUPER,
  UserRole.ADMIN,
  UserRole.SUPER_ADMIN,
] as const;

export const ACCESS_CONTROL_MANAGER_ROLES: readonly UserRole[] = [
  UserRole.SUPER,
  UserRole.SUPER_ADMIN,
] as const;
